import { format } from '@/utils/date-fns'
import { cn } from '@/lib/utils'
import { FAIcon, Tooltip } from '@/components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useState } from 'react'
import type { WeeklyWorkSum } from './utils/get-weekly-work-sums'

export interface WorkPeriodCalendarNode {
  id: string
  text: string
  color: string
  onClick?: () => void
  /** Opens the work-period edit log (from the edited-after-lock icon). */
  onLogClick?: () => void
  hasManualHours?: boolean
  editedAfterLock?: boolean
  /**
   * A weekly rest, drawn as the absence of work rather than as work: no colour
   * dot, a dashed outline and a moon, so a stretch away from home reads at a
   * glance without competing with the shifts around it.
   */
  variant?: 'work' | 'weekRest'
}

export interface WorkPeriodCalendarDay {
  date: Date
  weekNumber?: string | null
  grayed?: boolean
  nodes: WorkPeriodCalendarNode[]
}

interface WeekEntry {
  node: WorkPeriodCalendarNode
  startIdx: number
  span: number
  lane: number
  /** Same period continues on the previous week's last day (wraps in). */
  continuesLeft: boolean
  /** Same period continues on the next week's first day (wraps out). */
  continuesRight: boolean
}

// Lanes shown before collapsing into a "+N" chip. With exactly 4 lanes we show
// all of them (a "3 + 1 more" reads worse than just showing the 4th); only at
// 5+ do we collapse to COLLAPSED_LANES + "+N".
const COLLAPSED_LANES = 3
const OVERFLOW_AT = 5

/**
 * Split a flat, day-ordered list into rows of 7. Exported so callers deriving
 * per-week data (e.g. the weekly work sums) chunk exactly like the grid does.
 */
export function chunkIntoWeeks<T>(days: T[]): T[][] {
  const weeks: T[][] = []
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7))
  }
  return weeks
}

/**
 * Collapse the per-day nodes of one week into spanning entries: the same group
 * (node id) appearing on consecutive days becomes a single bar covering those
 * columns. Overlapping bars are stacked into lanes.
 *
 * A period that crosses the week boundary (e.g. Sunday into the next Monday) is
 * a single group split across two rows. We flag those halves via
 * `continuesLeft`/`continuesRight` — using the ids present on the adjacent
 * week's touching day — so they can be rendered as one wrapped bar rather than
 * two disconnected ones.
 */
function buildWeekEntries(
  week: WorkPeriodCalendarDay[],
  prevWeekLastDayIds: Set<string>,
  nextWeekFirstDayIds: Set<string>
): {
  entries: WeekEntry[]
  laneCount: number
} {
  const byId = new Map<
    string,
    { node: WorkPeriodCalendarNode; start: number; end: number }
  >()

  week.forEach((day, dayIdx) => {
    day.nodes.forEach(node => {
      const existing = byId.get(node.id)
      if (existing) {
        existing.end = dayIdx
      } else {
        byId.set(node.id, { node, start: dayIdx, end: dayIdx })
      }
    })
  })

  const raw = [...byId.values()].sort(
    (a, b) => a.start - b.start || b.end - b.start - (a.end - a.start)
  )

  const laneEnds: number[] = []
  const entries: WeekEntry[] = raw.map(item => {
    let lane = laneEnds.findIndex(end => end < item.start)
    if (lane === -1) {
      lane = laneEnds.length
      laneEnds.push(item.end)
    } else {
      laneEnds[lane] = item.end
    }
    return {
      node: item.node,
      startIdx: item.start,
      span: item.end - item.start + 1,
      lane,
      continuesLeft: item.start === 0 && prevWeekLastDayIds.has(item.node.id),
      continuesRight: item.end === 6 && nextWeekFirstDayIds.has(item.node.id),
    }
  })

  return { entries, laneCount: laneEnds.length }
}

const WeekSummaryTooltip: React.FC<{
  weekSum: WeeklyWorkSum
  summaryLabel: string
  totalLabel: string
}> = ({ weekSum, summaryLabel, totalLabel }) => (
  <div className="min-w-[10rem] text-xs">
    <div className="mb-1 font-semibold">{summaryLabel}</div>
    <table className="w-full border-collapse tabular-nums">
      <tbody>
        {weekSum.activitySums.map(activitySum => (
          <tr key={activitySum.name}>
            <td className="pr-3 text-left">{activitySum.name}</td>
            <td className="text-right">{activitySum.sum}</td>
          </tr>
        ))}
        <tr className="border-t border-white/30 font-semibold">
          <td className="pr-3 pt-1 text-left">{totalLabel}</td>
          <td className="pt-1 text-right">{weekSum.totalWorkTime}</td>
        </tr>
      </tbody>
    </table>
  </div>
)

const WeekRow: React.FC<{
  week: WorkPeriodCalendarDay[]
  isLast: boolean
  prevWeekLastDayIds: Set<string>
  nextWeekFirstDayIds: Set<string>
  showLessLabel: string
  manualHoursLabel: string
  editedAfterLockLabel: string
  weekLabel: string
  weekSum?: WeeklyWorkSum
  weekSummaryLabel: string
  totalWorkLabel: string
}> = ({
  week,
  isLast,
  prevWeekLastDayIds,
  nextWeekFirstDayIds,
  showLessLabel,
  manualHoursLabel,
  editedAfterLockLabel,
  weekLabel,
  weekSum,
  weekSummaryLabel,
  totalWorkLabel,
}) => {
  const [expanded, setExpanded] = useState(false)
  const { entries, laneCount } = buildWeekEntries(
    week,
    prevWeekLastDayIds,
    nextWeekFirstDayIds
  )

  const hasOverflow = laneCount >= OVERFLOW_AT
  const collapsed = hasOverflow && !expanded
  const visibleLanes = collapsed ? COLLAPSED_LANES : laneCount

  // Per-day count of entries hidden in the collapsed lanes.
  const hiddenPerDay = Array.from({ length: 7 }, () => 0)
  if (collapsed) {
    entries.forEach(entry => {
      if (entry.lane >= visibleLanes) {
        for (let d = entry.startIdx; d < entry.startIdx + entry.span; d++) {
          hiddenPerDay[d] = (hiddenPerDay[d] ?? 0) + 1
        }
      }
    })
  }

  const overflowRow = collapsed // "+N" row sits just below the visible lanes
  const collapseRow = hasOverflow && expanded // "show less" row at the bottom
  const extraRow = overflowRow || collapseRow ? ' 1.9rem' : ''

  return (
    <div
      className={cn(
        'relative grid grid-cols-7',
        !isLast && 'border-b border-gray-200'
      )}
      style={{
        minHeight: '8rem',
        gridTemplateRows: `auto repeat(${visibleLanes}, 1.9rem)${extraRow} 1fr`,
      }}
    >
      {/* Weekly work-sum info icon, pinned to the right edge of the row. */}
      {weekSum?.hasEntries && (
        <Tooltip
          content={
            <WeekSummaryTooltip
              weekSum={weekSum}
              summaryLabel={weekSummaryLabel}
              totalLabel={totalWorkLabel}
            />
          }
        >
          <button
            type="button"
            aria-label={weekSummaryLabel}
            className="absolute right-1.5 top-1.5 z-20 flex items-center text-gray-400 transition-colors hover:text-gray-600"
          >
            <FAIcon icon="info-circle" className="text-sm" />
          </button>
        </Tooltip>
      )}

      {/* Full-height day backgrounds (borders + grayed state). */}
      {week.map((day, dayIdx) => (
        <div
          key={`bg-${day.date.toISOString()}`}
          style={{ gridColumn: dayIdx + 1, gridRow: '1 / -1' }}
          className={cn(
            dayIdx < 6 && 'border-r border-gray-200',
            day.grayed && 'bg-gray-50/70'
          )}
        />
      ))}

      {/* Date numbers + week number. */}
      {week.map((day, dayIdx) => (
        <div
          key={`num-${day.date.toISOString()}`}
          style={{ gridColumn: dayIdx + 1, gridRow: 1 }}
          className="z-10 flex items-baseline justify-between px-2 pt-1.5"
        >
          <span
            className={cn(
              'text-sm font-semibold',
              day.grayed ? 'text-gray-300' : 'text-gray-700'
            )}
          >
            {day.date.getDate()}
          </span>
          {day.weekNumber && (
            <span className="text-[0.7rem] font-semibold uppercase tracking-wide text-gray-500">
              {weekLabel} {day.weekNumber}
            </span>
          )}
        </div>
      ))}

      {/* Spanning entry bars (visible lanes only). */}
      {entries
        .filter(entry => entry.lane < visibleLanes)
        .map((entry, entryIdx) => (
          <div
            key={`${entry.node.id}-${entryIdx}`}
            role={entry.node.onClick ? 'button' : undefined}
            tabIndex={entry.node.onClick ? 0 : undefined}
            title={entry.node.text}
            onClick={entry.node.onClick}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                entry.node.onClick?.()
              }
            }}
            style={{
              gridColumn: `${entry.startIdx + 1} / span ${entry.span}`,
              gridRow: entry.lane + 2,
            }}
            className={cn(
              'z-10 my-px flex min-w-0 items-center gap-2 border border-gray-400 bg-white px-2 text-left text-xs text-gray-700 transition-colors',
              entry.node.onClick &&
                'cursor-pointer hover:border-gray-500 hover:bg-gray-50',
              entry.node.variant === 'weekRest' &&
                'border-dashed border-stone-300 bg-stone-100 text-stone-500 hover:border-stone-400 hover:bg-stone-200',
              // The wrapping edge bleeds to the calendar boundary (no margin,
              // no border, flat corner) so the two halves read as one period;
              // the other edge keeps the normal rounded, inset look.
              entry.continuesLeft
                ? 'ml-0 rounded-l-none border-l-0'
                : 'ml-1 rounded-l-md',
              entry.continuesRight
                ? 'mr-0 rounded-r-none border-r-0'
                : 'mr-1 rounded-r-md'
            )}
          >
            {entry.continuesLeft && (
              <FAIcon
                icon="chevron-left"
                className="-ml-0.5 shrink-0 text-[0.6rem] text-gray-400"
              />
            )}
            {entry.node.variant === 'weekRest' ? (
              <FAIcon
                icon="moon"
                type="solid"
                className="shrink-0 text-[0.7rem]"
                style={{ color: entry.node.color }}
              />
            ) : (
              <span
                className="size-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: entry.node.color }}
              />
            )}
            <span className="min-w-0 flex-1 truncate">{entry.node.text}</span>
            {(entry.node.hasManualHours || entry.node.editedAfterLock) && (
              <span className="flex shrink-0 items-center gap-1">
                {entry.node.hasManualHours && (
                  <Tooltip content={manualHoursLabel}>
                    <span className="flex items-center">
                      <FAIcon
                        icon="info-circle"
                        className="text-[0.7rem] text-red-500"
                      />
                    </span>
                  </Tooltip>
                )}
                {entry.node.editedAfterLock && (
                  <Tooltip content={editedAfterLockLabel}>
                    <button
                      type="button"
                      className="flex items-center"
                      onClick={event => {
                        if (entry.node.onLogClick) {
                          event.stopPropagation()
                          entry.node.onLogClick()
                        }
                      }}
                    >
                      <FAIcon
                        icon="exclamation-triangle"
                        className="text-[0.7rem] text-amber-500"
                      />
                    </button>
                  </Tooltip>
                )}
              </span>
            )}
            {entry.continuesRight && (
              <FAIcon
                icon="chevron-right"
                className="-mr-0.5 shrink-0 text-[0.6rem] text-gray-400"
              />
            )}
          </div>
        ))}

      {/* "+N" chips per day with hidden entries — clicking expands the week. */}
      {collapsed &&
        hiddenPerDay.map((count, dayIdx) =>
          count > 0 ? (
            <button
              key={`more-${dayIdx}`}
              type="button"
              onClick={() => setExpanded(true)}
              style={{ gridColumn: dayIdx + 1, gridRow: visibleLanes + 2 }}
              className="z-10 mx-1 my-px flex items-center rounded-md px-2 text-left text-[0.7rem] font-medium text-gray-500 transition-colors hover:bg-gray-100"
            >
              +{count}
            </button>
          ) : null
        )}

      {/* "Show less" when the week is expanded. */}
      {collapseRow && (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          style={{ gridColumn: '1 / -1', gridRow: laneCount + 2 }}
          className="z-10 mx-1 my-px flex items-center gap-1 rounded-md px-2 text-left text-[0.7rem] font-medium text-gray-500 transition-colors hover:bg-gray-100"
        >
          <FAIcon icon="chevron-up" />
          {showLessLabel}
        </button>
      )}
    </div>
  )
}

/**
 * Month grid for the work-period overview. A purpose-built, Tailwind
 * replacement for the shared (styled-components) Calendar: roomier day cells,
 * a colored status dot per entry, and bars that span across the days a work
 * period covers.
 */
export const WorkPeriodCalendar: React.FC<{
  data: WorkPeriodCalendarDay[]
  /** Per-week aggregated work sums, aligned by index with the calendar weeks. */
  weeklySums?: WeeklyWorkSum[]
}> = ({ data, weeklySums }) => {
  const translations = useTranslate({
    showLess: 'common.show-less',
    manualHours: 'salary.this-work-day-has-manual-hours-from-time-entries',
    editedAfterLock: 'salary.edited-after-lock',
    week: 'common.week',
    weekSummary: 'activities.work-summary',
    // `activities.weekly-total-working-time` is the AETR weekly working time
    // shown in Remaining Driving Time — a different quantity. This row is the
    // salary total work for the week, labelled like everywhere else it appears.
    totalWork: 'common.total-work',
  })
  const weeks = chunkIntoWeeks(data)
  const weekdayHeaders = data.slice(0, 7)

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-100">
        {weekdayHeaders.map(day => (
          <div
            key={`weekday-${day.date.toISOString()}`}
            className="px-2 py-2.5 text-center text-xs font-bold uppercase tracking-wider text-gray-600"
          >
            {format(day.date, 'EEE')}
          </div>
        ))}
      </div>

      {weeks.map((week, weekIdx) => (
        <WeekRow
          key={week[0]?.date.toISOString() ?? weekIdx}
          week={week}
          isLast={weekIdx === weeks.length - 1}
          // Ids on the touching day of the neighbouring week let a period that
          // wraps across the week boundary (e.g. Sunday → next Monday) render
          // as one continuous bar instead of two disconnected ones.
          prevWeekLastDayIds={
            new Set(weeks[weekIdx - 1]?.[6]?.nodes.map(node => node.id))
          }
          nextWeekFirstDayIds={
            new Set(weeks[weekIdx + 1]?.[0]?.nodes.map(node => node.id))
          }
          showLessLabel={translations.showLess}
          manualHoursLabel={translations.manualHours}
          editedAfterLockLabel={translations.editedAfterLock}
          weekLabel={translations.week}
          weekSum={weeklySums?.[weekIdx]}
          weekSummaryLabel={translations.weekSummary}
          totalWorkLabel={translations.totalWork}
        />
      ))}
    </div>
  )
}
